import { Badge, Box, Group, SimpleGrid, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import type { Device } from '../../../../../../shared/types/device';
import { InfoField } from '../../../../../ui/infoField/InfoField';
import { getLocaleDate, handleChangeLocaleTime } from '../../../../../../shared/utils/time';
import { modelFilterHook } from '../../../../../../shared/utils/modelFilter';
import ExpandServiceModal from './expandServiceModal';

export default function DeviceBasicInfo(props: { data: Device }) {
  const { data } = props;
  const [extendServiceOpened, { open: openExtendService, close: closeExtendService }] = useDisclosure(false);
  const navigate = useNavigate();

  const startDt = handleChangeLocaleTime(data.serviceStartDt)?.substring(0, 10) || '정보 없음';
  const endDt = handleChangeLocaleTime(data.exp)?.substring(0, 10) || '정보 없음';
  const today = dayjs();
  const endDate = dayjs(getLocaleDate(data.exp));
  const diff = endDate.diff(today, 'day');
  const renderDateDiff = (diff: number) => {
    if (diff > 0) {
      return 'D-' + diff;
    }
    return 'D+' + Math.abs(diff);
  };

  return (
    <div>
      <Text fw={600} mb="xs">
        디바이스 기본 정보
      </Text>
      <Box
        style={{
          borderTop: '1px solid var(--mantine-color-gray-3)',
          borderLeft: '1px solid var(--mantine-color-gray-3)',
        }}
      >
        <SimpleGrid cols={4} spacing={0} verticalSpacing={0}>
          <InfoField label="MAC번호">{data?.mac}</InfoField>
          <InfoField label="디바이스 종류">{modelFilterHook(data?.deviceCd, data?.deviceSubCd)}</InfoField>
          <InfoField label="S/N">{data?.serialNum}</InfoField>
          <InfoField label="링크 종류">
            {
              <Group
                style={(theme) => ({
                  fontSize: theme.fontSizes.sm,
                })}
                gap={4}
                justify="center"
              >
                <Text size="sm">{data.link1 ?? '-'}</Text>/<Text size="sm">{data.link2 ?? '-'}</Text>
              </Group>
            }
          </InfoField>
          <InfoField label="서비스 이용기간">
            <Text size="sm" mr={8}>
              {startDt} ~ {endDt}{' '}
              {data.exp && (
                <Text component="span" c="red" fw={600}>
                  ({renderDateDiff(diff)})
                </Text>
              )}
            </Text>
            <Badge
              variant="light"
              size="md"
              radius="xl"
              color="blue"
              style={{
                cursor: 'pointer',
              }}
              onClick={openExtendService}
            >
              입력
            </Badge>
          </InfoField>
          <InfoField label="출고 상태">{data?.shipment ? '출고 완료' : '출고 대기'}</InfoField>
          <InfoField label="등록일">{handleChangeLocaleTime(data?.regDt)}</InfoField>
          <InfoField label="네트워크 종류">{data?.networkType}</InfoField>
          <InfoField label="영업 담당자">{data?.salesManager}</InfoField>
          <InfoField label="펌웨어 버전">{data?.fwVer}</InfoField>
          <InfoField label="모뎀/라우터">{data?.modem}</InfoField>
          <InfoField label="단말기 일련번호">
            <Text
              size="sm"
              td="underline"
              style={{
                cursor: 'pointer',
              }}
              onClick={() => navigate(`/admin/management/modem/details/${data?.modemId}`)}
            >
              {data?.modemNo}
            </Text>
          </InfoField>

          <InfoField label="최근 데이터 업데이트 일자">{handleChangeLocaleTime(data?.latestModDt)}</InfoField>
          <InfoField label="비고">{data?.memo}</InfoField>
        </SimpleGrid>
      </Box>
      <ExpandServiceModal opened={extendServiceOpened} onClose={closeExtendService} data={data} />
    </div>
  );
}
